import { FiltersProvider } from '@Sage/ssm-web5';
import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import { EntityDetailActions } from 'actions';

import { RouteOnMount } from 'utils/routes';
import { CAMPAIGNS_MARKETING } from 'constants/Entities';
import DetailLayout from 'containers/components/layouts/ContentDetailLayout';
import FiltersLayout from 'containers/components/layouts/FiltersLayout';
import Menu from './Menu';
import Content from './Content';
import ContentNavigator from './ContentNavigator';
import CONFIG from './contents/TableConfig';
import TemplatesContent from './templates/Content';
import { MarketingTemplatesRefreshProvider } from './templates/MarketingTemplatesRefreshContext';
import TemplatesListMenu from './templates/components/TemplatesListMenu';
import TemplateSelectorHeader from './templates/create/components/TemplateSelectorHeader';
import TemplateHtmlEditor from './templates/TemplateHtmlEditor';
import TemplateVisualEditor from './templates/TemplateVisualEditor';
import {
    isMarketingTemplatesListMenuPath,
    isMarketingTemplatesSelectorHeaderPath,
} from './templates/marketingTemplatesPaths';

import { MarketingManagementGuard } from './wrappers/MarketingManagementRoute';
import DomainVerificationBanner from './components/DomainVerificationBanner';
import MarketingCampaignsDetail from './MarketingCampaignsDetail';

const isMarketingTemplatesPath = (pathname) =>
    pathname.startsWith('/campaigns/marketing/templates');

const MarketingCampaigns = ({ onManageSubscribers }) => {
    const dispatch = useDispatch();
    const openDetail = bindActionCreators(EntityDetailActions, dispatch).open;
    const currentDetailId = useRef();
    const config = CONFIG();
    const { pathname } = useLocation();
    const isTemplatesRoute = isMarketingTemplatesPath(pathname);

    const [htmlEditorState, setHtmlEditorState] = useState(null);
    const [visualEditorState, setVisualEditorState] = useState(null);

    const handleOpenHtmlEditor = useCallback((initialState) => {
        setHtmlEditorState(initialState || {});
        setVisualEditorState(null);
    }, []);

    const handleOpenVisualEditor = useCallback((initialState) => {
        setVisualEditorState(initialState || {});
        setHtmlEditorState(null);
    }, []);

    const handleCloseHtmlEditor = useCallback(() => {
        setHtmlEditorState(null);
    }, []);

    const handleCloseVisualEditor = useCallback(() => {
        setVisualEditorState(null);
    }, []);

    const templatesHeader = (() => {
        if (isMarketingTemplatesSelectorHeaderPath(pathname)) {
            return <TemplateSelectorHeader />;
        }
        if (isMarketingTemplatesListMenuPath(pathname)) {
            return <TemplatesListMenu />;
        }
        return <TemplatesListMenu />;
    })();

    const isHtmlEditorOpen = htmlEditorState !== null;
    const isVisualEditorOpen = visualEditorState !== null;

    return (
        <FiltersProvider>
            <DetailLayout
                header={isTemplatesRoute ? templatesHeader : <Menu config={config} />}
                navigator={<ContentNavigator />}
                postHeader={<DomainVerificationBanner />}
            >
                {isTemplatesRoute ? (
                    <MarketingTemplatesRefreshProvider>
                        <div className="marketing-templates__content-wrapper">
                            <TemplatesContent
                                onOpenHtmlEditor={handleOpenHtmlEditor}
                                onOpenVisualEditor={handleOpenVisualEditor}
                            />

                            {/* Template editors overlay - covers from below main header */}
                            <div
                                className={classNames('marketing-templates__editor-overlay', {
                                    'marketing-templates__editor-overlay--open': isHtmlEditorOpen,
                                })}
                                aria-hidden={!isHtmlEditorOpen}
                            >
                                <TemplateHtmlEditor
                                    isOpen={isHtmlEditorOpen}
                                    initialHtml={htmlEditorState?.initialHtml || ''}
                                    initialTitle={htmlEditorState?.initialTitle || ''}
                                    templateId={htmlEditorState?.templateId}
                                    onExit={handleCloseHtmlEditor}
                                />
                            </div>
                            <div
                                className={classNames('marketing-templates__editor-overlay', {
                                    'marketing-templates__editor-overlay--open': isVisualEditorOpen,
                                })}
                                aria-hidden={!isVisualEditorOpen}
                            >
                                <TemplateVisualEditor
                                    isOpen={isVisualEditorOpen}
                                    strJson={visualEditorState?.strJson}
                                    initialTitle={visualEditorState?.initialTitle || ''}
                                    templateId={visualEditorState?.templateId}
                                    onExit={handleCloseVisualEditor}
                                />
                            </div>
                        </div>
                    </MarketingTemplatesRefreshProvider>
                ) : (
                    <FiltersLayout config={config}>
                        <Content />
                    </FiltersLayout>
                )}
            </DetailLayout>

            <MarketingCampaignsDetail />
            <Routes>
                <Route
                    index
                    element={
                        <RouteOnMount
                            title="page_title_marketing_campaigns"
                            onMount={() => {
                                currentDetailId.current = null;
                            }}
                        />
                    }
                />
                <Route
                    path="templates/new"
                    element={
                        <MarketingManagementGuard>
                            <RouteOnMount
                                title="label_marketing_campaigns_templates_create_template"
                                onMount={() => {
                                    currentDetailId.current = null;
                                }}
                            />
                        </MarketingManagementGuard>
                    }
                />
                <Route
                    path="templates/*"
                    element={
                        <MarketingManagementGuard>
                            <RouteOnMount
                                title="label_email_templates"
                                onMount={() => {
                                    currentDetailId.current = null;
                                }}
                            />
                        </MarketingManagementGuard>
                    }
                />
                <Route
                    path=":id/:tab?"
                    element={
                        <RouteOnMount
                            title="page_title_marketing_campaigns_details"
                            onMount={(params) => {
                                if (!params.id) return;
                                if (currentDetailId.current === params.id) {
                                    return;
                                }
                                currentDetailId.current = params.id;
                                openDetail(CAMPAIGNS_MARKETING, Number(params.id), false, {
                                    tab: params.tab,
                                });
                            }}
                        />
                    }
                />
            </Routes>
        </FiltersProvider>
    );
};

export default MarketingCampaigns;
